// Cross-tab presence for a logged-in vet.
//
// Why: the GetVideoVet auto-offline rules say a vet should only be marked
// offline when EVERY GetPetVet tab/window is closed (or, for screen-lock
// freeze, when the screen is actually locked AND no other live tab can keep
// answering). Both `useVetHeartbeat` (pagehide beacon) and
// `AvailableNowToggle` (Page Lifecycle 'freeze' auto-offline) need to ask
// the same question - "are there other live GetPetVet tabs for me right
// now?" - so we centralise it here instead of maintaining two parallel
// BroadcastChannel maps that can drift out of sync.
//
// Each tab announces itself ~every 2s on a per-user channel. Other tabs
// record `lastSeen`; entries older than the stale window are pruned so a
// crashed tab doesn't keep us thinking we're not the last one.

const PING_INTERVAL_MS = 2_000;
const STALE_MS = 5_000;

type Msg =
  | { type: "ping"; tabId: string }
  | { type: "hello"; tabId: string }
  | { type: "leaving"; tabId: string };

interface PresenceHandle {
  /** True if at least one OTHER live tab for this vet is currently announcing. */
  hasOtherLiveTabs: () => boolean;
  /** Tell siblings we're going away (call before tab close / route teardown). */
  announceLeaving: () => void;
  /** Stop pinging and close the channel. */
  stop: () => void;
}

const handlesByUser = new Map<string, { handle: PresenceHandle; refCount: number }>();

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

function createHandle(userId: string): PresenceHandle {
  if (typeof BroadcastChannel === "undefined") {
    return {
      hasOtherLiveTabs: () => false,
      announceLeaving: () => {},
      stop: () => {},
    };
  }
  const myId = newId();
  const siblings = new Map<string, number>();
  const bc = new BroadcastChannel(`vet-presence:${userId}`);

  const send = (type: Msg["type"]) => {
    try { bc.postMessage({ type, tabId: myId } satisfies Msg); } catch { /* ignore */ }
  };

  bc.onmessage = (ev) => {
    const m = ev.data as Msg | undefined;
    if (!m || !("tabId" in m) || m.tabId === myId) return;
    if (m.type === "leaving") { siblings.delete(m.tabId); return; }
    siblings.set(m.tabId, Date.now());
    // Reply to a brand-new tab so it learns about us immediately rather than
    // waiting for the next ping tick.
    if (m.type === "hello") send("ping");
  };

  send("hello");
  const ping = setInterval(() => {
    send("ping");
    const cutoff = Date.now() - STALE_MS;
    for (const [id, lastSeen] of siblings) if (lastSeen < cutoff) siblings.delete(id);
  }, PING_INTERVAL_MS);

  return {
    hasOtherLiveTabs: () => {
      const cutoff = Date.now() - STALE_MS;
      for (const lastSeen of siblings.values()) if (lastSeen >= cutoff) return true;
      return false;
    },
    announceLeaving: () => send("leaving"),
    stop: () => {
      clearInterval(ping);
      send("leaving");
      bc.close();
      siblings.clear();
    },
  };
}

/**
 * Acquire a refcounted presence handle for this vet. Multiple components in
 * the same tab share the same handle; the channel is only torn down when the
 * last subscriber releases it.
 */
export function acquireVetTabPresence(userId: string): PresenceHandle & { release: () => void } {
  const existing = handlesByUser.get(userId);
  if (existing) {
    existing.refCount += 1;
    return {
      ...existing.handle,
      release: () => releaseVetTabPresence(userId),
    };
  }
  const handle = createHandle(userId);
  handlesByUser.set(userId, { handle, refCount: 1 });
  return {
    ...handle,
    release: () => releaseVetTabPresence(userId),
  };
}

function releaseVetTabPresence(userId: string) {
  const entry = handlesByUser.get(userId);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    entry.handle.stop();
    handlesByUser.delete(userId);
  }
}
